import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, Eye, Edit, Trash, Filter } from "lucide-react";
import dayjs from "dayjs";
import { Select } from "antd";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ConfirmPopupModal } from "../../components/ui/dialogModal";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { APPRAISAL_CYCLES } from "../../constants/api";

import { useAppraisalPermissions } from "../../hooks/useAppraisalRole";

const cycleStatusStyles = {
    draft: "bg-gray-100 text-gray-700 dark:bg-gray-700/40 dark:text-gray-200",
    active: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    closed: "bg-slate-200 text-slate-700 dark:bg-slate-700/40 dark:text-slate-200",
};

const AppraisalCycleList = () => {
    const navigate = useNavigate();
    const { fetch, remove } = useApiMethods();
    const { canAddCycle, canEditCycle } = useAppraisalPermissions();

    const [cycles, setCycles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [deleteTarget, setDeleteTarget] = useState(null);

    // Debounce so each keystroke doesn't ping the server.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 350);
        return () => clearTimeout(t);
    }, [search]);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const filters = {};
            if (statusFilter) filters.status = statusFilter;
            if (debouncedSearch) filters.search = debouncedSearch;
            const res = await fetch(APPRAISAL_CYCLES, filters);
            setCycles(res?.data || []);
        } catch (_) {
            ToastMsg("error", "Failed to load cycles");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, debouncedSearch]);

    useEffect(() => { loadData(); }, [loadData]);

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            const res = await remove(`${APPRAISAL_CYCLES}?id=${deleteTarget.id}`);
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Cycle removed");
                setDeleteTarget(null);
                loadData();
            } else {
                ToastMsg("error", res?.message || "Failed to remove cycle");
            }
        } catch (_) {
            ToastMsg("error", "Failed to remove cycle");
        }
    };

    return (
        <ContainerCard scrollable>
            <ModuleTitleCard
                Icon={CalendarClock}
                title="Appraisal Cycles"
                subtitle="Define the periods within which performance reviews are run"
                totalRecords={cycles.length}
                buttonEnable={canAddCycle}
                buttonName="New Cycle"
                setOpenDialog={() => navigate("/appraisal/cycles/new")}
            />

            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 mt-2">
                <div className="flex flex-wrap items-center gap-2 pb-3">
                <div className="flex items-center gap-2 flex-1 min-w-[180px]">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search by name"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="max-w-xs"
                    />
                </div>
                <Select
                    size="large"
                    placeholder="Status"
                    allowClear
                    value={statusFilter || undefined}
                    onChange={(v) => setStatusFilter(v || "")}
                    options={[
                        { value: "draft", label: "Draft" },
                        { value: "active", label: "Active" },
                        { value: "closed", label: "Closed" },
                    ]}
                    style={{ minWidth: 140 }}
                />
            </div>

                <div className="overflow-auto">
                {loading ? (
                    <div className="text-center py-10 text-muted-foreground">Loading…</div>
                ) : cycles.length === 0 ? (
                    <div className="text-center py-10 text-muted-foreground">No cycles found.</div>
                ) : (
                    <div className="overflow-x-auto rounded-md border border-border">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/40">
                                <tr className="text-left text-muted-foreground">
                                    <th className="px-3 py-2 font-medium">Name</th>
                                    <th className="px-3 py-2 font-medium hidden sm:table-cell">Period</th>
                                    <th className="px-3 py-2 font-medium hidden md:table-cell">Self-Eval Window</th>
                                    <th className="px-3 py-2 font-medium">Status</th>
                                    <th className="px-3 py-2 font-medium text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cycles.map((c) => (
                                    <tr key={c.id} className="border-t border-border hover:bg-muted/20">
                                        <td className="px-3 py-2 font-medium text-foreground">
                                            <button
                                                className="hover:text-sidebar-primary text-left"
                                                onClick={() => navigate(`/appraisal/cycles/${c.id}`)}
                                            >
                                                {c.name}
                                            </button>
                                            <p className="text-xs text-muted-foreground line-clamp-1 sm:hidden">
                                                {dayjs(c.period_start).format("DD MMM")} – {dayjs(c.period_end).format("DD MMM YYYY")}
                                            </p>
                                        </td>
                                        <td className="px-3 py-2 hidden sm:table-cell">
                                            {dayjs(c.period_start).format("DD MMM YYYY")} – {dayjs(c.period_end).format("DD MMM YYYY")}
                                        </td>
                                        <td className="px-3 py-2 hidden md:table-cell">
                                            {dayjs(c.self_eval_start).format("DD MMM")} – {dayjs(c.self_eval_end).format("DD MMM YYYY")}
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full capitalize ${cycleStatusStyles[c.status] || cycleStatusStyles.draft}`}>
                                                {c.status}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="flex items-center justify-end gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => navigate(`/appraisal/cycles/${c.id}`)}
                                                    title="View"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </Button>
                                                {canEditCycle && (
                                                    <>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => navigate(`/appraisal/cycles/edit/${c.id}`)}
                                                            title="Edit"
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => setDeleteTarget(c)}
                                                            title="Delete"
                                                            className="text-red-500 hover:text-red-600"
                                                        >
                                                            <Trash className="w-4 h-4" />
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                </div>
            </div>

            <ConfirmPopupModal
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                title="Delete Cycle"
                description={`Are you sure you want to delete "${deleteTarget?.name}"? Existing appraisals in this cycle will lose their reference.`}
                destructive
                confirmLabel="Delete"
                type="danger"
            />
        </ContainerCard>
    );
};

export default AppraisalCycleList;
